import { call, put, takeLatest } from '@spcc/core'
import type { PayloadAction } from '@reduxjs/toolkit'
import { weighbridgeActions } from './weighbridge.slice'
import { shipmentsApi } from '../infrastructure'
import type { StartProcessingRequest, StartProcessingResponse } from '../api/scan-types'
import type { WeighbridgeTicket, FirstWeighRequest, SecondWeighRequest, MarkDeliveredRequest } from '../api/weighbridge-types'

function* firstWeighSaga(action: PayloadAction<FirstWeighRequest>) {
  try {
    const ticket: WeighbridgeTicket = yield call(shipmentsApi.firstWeigh, action.payload)
    yield put(weighbridgeActions.firstWeighSuccess(ticket))
  } catch (error: any) {
    const message = error.response?.data?.error?.message || error.message || 'First weigh failed'
    yield put(weighbridgeActions.firstWeighFailure(message))
  }
}

function* secondWeighSaga(action: PayloadAction<SecondWeighRequest>) {
  try {
    const ticket: WeighbridgeTicket = yield call(shipmentsApi.secondWeigh, action.payload)
    yield put(weighbridgeActions.secondWeighSuccess(ticket))
  } catch (error: any) {
    const message = error.response?.data?.error?.message || error.message || 'Second weigh failed'
    yield put(weighbridgeActions.secondWeighFailure(message))
  }
}

function* markDeliveredSaga(action: PayloadAction<MarkDeliveredRequest>) {
  try {
    const { orderNumber, gateCode } = action.payload

    if (gateCode) {
      // Use QR scan endpoint for full SAP exit processing
      yield call(shipmentsApi.qrScanReading, orderNumber, gateCode)
    } else {
      // Fallback to original fulfillment endpoint
      yield call(shipmentsApi.markDelivered, action.payload)
    }

    yield put(weighbridgeActions.markDeliveredSuccess())
  } catch (error: any) {
    const message = error.response?.data?.error?.message || error.message || 'Mark delivered failed'
    yield put(weighbridgeActions.markDeliveredFailure(message))
  }
}

function* startProcessingSaga(action: PayloadAction<StartProcessingRequest>) {
  try {
    const { gateCode, orderNumber } = action.payload

    let result: StartProcessingResponse

    if (gateCode && orderNumber) {
      // Use QR scan endpoint for full SAP flow (SO creation + contract resolution)
      const response: { success: boolean; data: any; meta: any } = yield call(
        shipmentsApi.qrScanReading,
        orderNumber,
        gateCode
      )

      result = {
        order: response.data,
        statusUpdate: response.meta?.orderStatusUpdate ?? { before: null, after: null, updated: false },
        balanceCheck: response.meta?.balanceCheck,
      }
    } else {
      // Fallback to original fulfillment endpoint
      result = yield call(shipmentsApi.startProcessing, action.payload)
    }

    yield put(weighbridgeActions.startProcessingSuccess(result))
  } catch (error: any) {
    const message = error.response?.data?.error?.message || error.message || 'Start processing failed'
    const balanceCheck = error.response?.data?.error?.details?.balanceCheck

    // If balance check info is available, include it in the failure payload
    if (balanceCheck) {
      yield put(weighbridgeActions.startProcessingFailure({ message, balanceCheck }))
    } else {
      yield put(weighbridgeActions.startProcessingFailure(message))
    }
  }
}

export function* weighbridgeSaga() {
  yield takeLatest(weighbridgeActions.firstWeigh.type, firstWeighSaga)
  yield takeLatest(weighbridgeActions.secondWeigh.type, secondWeighSaga)
  yield takeLatest(weighbridgeActions.markDelivered.type, markDeliveredSaga)
  yield takeLatest(weighbridgeActions.startProcessing.type, startProcessingSaga)
}
